import { isPlausibleAddress } from '@/app/composeIntent';
import type { ComposeSeed } from './ComposeDialog';

/**
 * A `mailto:` link clicked in a message body, as a composer seed (RFC 6068).
 *
 * The link is the SENDER's text, so it is parsed as input:
 *
 * - **Addresses** (the path, `to=` and `cc=`) are split on commas and each
 *   one must pass `isPlausibleAddress` — no controls, one `local@domain` —
 *   or it is dropped. A header smuggled through a recipient is the obvious
 *   attack, and the recipient fields are headers.
 * - **`bcc=` is ignored.** A blind copy is the one field a reader would not
 *   notice before pressing Send, which makes it the one a link must not set.
 * - **Subject** loses every control character (CR/LF included), so it stays
 *   one line; **body** keeps its line breaks and nothing else. Both are
 *   capped, and both are plain text — the composer never parses them as HTML.
 * - `+` is a literal plus in a mailto, not a space (RFC 6068 §5), which is
 *   why this does not use `URLSearchParams`.
 *
 * Nothing is sent: the composer opens with what the link asked for, and the
 * reader sees all of it before deciding.
 */
export function mailtoSeed(href: string): ComposeSeed {
  const withoutScheme = href.trim().replace(/^mailto:/i, '');
  const queryAt = withoutScheme.indexOf('?');
  const path = queryAt >= 0 ? withoutScheme.slice(0, queryAt) : withoutScheme;
  const query = queryAt >= 0 ? withoutScheme.slice(queryAt + 1) : '';

  const to: string[] = splitAddresses(decode(path));
  const cc: string[] = [];
  let subject = '';
  let body = '';
  for (const pair of query.split('&')) {
    if (pair === '') continue;
    const eq = pair.indexOf('=');
    const name = decode(eq >= 0 ? pair.slice(0, eq) : pair).toLowerCase();
    const value = decode(eq >= 0 ? pair.slice(eq + 1) : '');
    if (name === 'to') to.push(...splitAddresses(value));
    else if (name === 'cc') cc.push(...splitAddresses(value));
    else if (name === 'subject' && subject === '') subject = oneLine(value).slice(0, MAX_SUBJECT);
    else if (name === 'body' && body === '') body = plainBody(value).slice(0, MAX_BODY);
  }

  return {
    mode: 'new',
    ...(to.length > 0 ? { to: unique(to).slice(0, MAX_RECIPIENTS).join(', ') } : {}),
    ...(cc.length > 0 ? { cc: unique(cc).slice(0, MAX_RECIPIENTS).join(', ') } : {}),
    ...(subject !== '' ? { subject } : {}),
    ...(body !== '' ? { body } : {}),
  };
}

const MAX_SUBJECT = 250;
const MAX_BODY = 10_000;
const MAX_RECIPIENTS = 20;

/** Percent-decoding that never throws: a malformed escape is kept as typed. */
function decode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function splitAddresses(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => isPlausibleAddress(part));
}

function unique(addresses: string[]): string[] {
  const seen = new Set<string>();
  return addresses.filter((address) => {
    const key = address.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Every control character becomes a space: a subject is one line. */
function oneLine(value: string): string {
  let out = '';
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    out += code < 0x20 || code === 0x7f ? ' ' : ch;
  }
  return out.replace(/\s+/g, ' ').trim();
}

/** Line breaks survive (as `\n`); every other control character is dropped. */
function plainBody(value: string): string {
  let out = '';
  for (const ch of value.replace(/\r\n?/g, '\n')) {
    const code = ch.codePointAt(0) ?? 0;
    if (ch === '\n' || ch === '\t' || (code >= 0x20 && code !== 0x7f)) out += ch;
  }
  return out;
}
